import type { SharedPageProps } from "@inertiajs/core";

import { isAppearance } from "@/lib/state/appearance";

/**
 * The page-boundary guard for the shared Inertia props.
 *
 * Inertia serialises whatever the server returns, ships it over XHR, and React
 * hydrates it as props. Every type this application generates from its Data
 * classes is erased before that data exists, so the props arriving in the
 * browser are the one place where a mismatch between the server's contract and
 * the client's belief can still be observed. This module observes it once, at
 * the seam, and everything downstream reads the props as the generated types
 * describe them.
 *
 * It deliberately checks presence and kind, not the full nested shape. The
 * generated types already describe the interiors, and restating them here would
 * be a second contract that drifts. What static types cannot rule out is the
 * envelope arriving incomplete: a page prop that reuses a shared prop's name
 * replaces it for that page, tenancy or authentication could stop filling a
 * field, and a prop can be renamed on the server while the generated types are
 * regenerated but a stale bundle is still being served.
 *
 * The check list is keyed by `keyof SharedPageProps`, so a prop added to the
 * contract that is not checked here is a compile error rather than a silent
 * gap.
 */

type PropCheck = (value: unknown) => string | null;

const describeValue = (value: unknown): string => {
  if (value === null) {
    return "null";
  }

  if (Array.isArray(value)) {
    return "an array";
  }

  return typeof value;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const aRecord: PropCheck = (value) =>
  isRecord(value) ? null : `should be an object, received ${describeValue(value)}`;

const aNonEmptyString: PropCheck = (value) => {
  if (typeof value !== "string") {
    return `should be a string, received ${describeValue(value)}`;
  }

  return value === "" ? "should not be empty" : null;
};

const aBoolean: PropCheck = (value) =>
  typeof value === "boolean" ? null : `should be a boolean, received ${describeValue(value)}`;

const withFields =
  (fields: Readonly<Record<string, PropCheck>>): PropCheck =>
  (value) => {
    if (!isRecord(value)) {
      return `should be an object, received ${describeValue(value)}`;
    }

    const failures = Object.entries(fields)
      .map(([field, check]) => {
        const failure = Object.hasOwn(value, field) ? check(value[field]) : "is missing";

        return failure === null ? null : `${field} ${failure}`;
      })
      .filter((failure) => failure !== null);

    return failures.length === 0 ? null : `has ${failures.join(", ")}`;
  };

const nullOr =
  (check: PropCheck): PropCheck =>
  (value) =>
    value === null ? null : check(value);

const anArrayOfStrings: PropCheck = (value) => {
  if (!Array.isArray(value)) {
    return `should be an array, received ${describeValue(value)}`;
  }

  return value.every((entry) => typeof entry === "string") ? null : "should hold only strings";
};

const anArray: PropCheck = (value) =>
  Array.isArray(value) ? null : `should be an array, received ${describeValue(value)}`;

/**
 * Every upload surface the shared catalog carries. A surface added to
 * `UploadConstraintCatalogData` that is missing here is a compile error, which
 * is what makes this list safe to check against.
 */
const UPLOAD_SURFACES = {
  documentAttachment: aRecord,
  documentExtraction: aRecord,
  mediaPackage: aRecord,
  spreadsheet: aRecord,
  workImage: aRecord,
} satisfies Record<keyof App.Data.Upload.UploadConstraintCatalogData, PropCheck>;

const SHARED_PROP_CHECKS = {
  appearance: (value) =>
    typeof value === "string" && isAppearance(value)
      ? null
      : `should be a known appearance, received ${describeValue(value)}`,
  assets: withFields({ brand: withFields({ googleMapsLogoDarkGray: aNonEmptyString }) }),
  auth: withFields({
    actions: withFields({ canCreateViewingRooms: aBoolean }),
    collections: anArray,
    nav: withFields({ canAccessViewingRooms: aBoolean }),
    permissions: anArrayOfStrings,
    user: nullOr(withFields({ email: aNonEmptyString, id: () => null, name: aNonEmptyString })),
  }),
  centralAppUrl: aNonEmptyString,
  documentTypes: anArray,
  flash: aRecord,
  googleMaps: withFields({ mapId: aNonEmptyString }),
  movementOptions: withFields({
    creatableStatuses: anArray,
    intents: anArray,
    kinds: anArray,
  }),
  name: aNonEmptyString,
  posthog: withFields({ enabled: aBoolean }),
  productName: aNonEmptyString,
  sidebarOpen: aBoolean,
  tenant: nullOr(withFields({ identity: withFields({ id: aNonEmptyString }) })),
  uploadConstraints: withFields(UPLOAD_SURFACES),
} satisfies Record<keyof SharedPageProps, PropCheck>;

/**
 * Everything the incoming page props fail to honour, one sentence per prop.
 * Empty when the envelope matches the contract.
 */
export const collectSharedPropViolations = (props: unknown): readonly string[] => {
  if (!isRecord(props)) {
    return [`The page props should be an object, received ${describeValue(props)}.`];
  }

  return Object.entries(SHARED_PROP_CHECKS)
    .map(([name, check]) => {
      const failure = Object.hasOwn(props, name) ? check(props[name]) : "is missing";

      return failure === null ? null : `\`${name}\` ${failure}.`;
    })
    .filter((violation) => violation !== null);
};

/**
 * Check one page's props against the shared contract.
 *
 * A violation is a defect in the server contract, not a condition the tree can
 * recover from, so development fails on it immediately and loudly. Production
 * reports it and renders anyway: a member mid-task is better served by a page
 * that may be missing one affordance than by a blank screen.
 */
export const guardSharedProps = (props: unknown, report: (error: Error) => void): void => {
  const violations = collectSharedPropViolations(props);

  if (violations.length === 0) {
    return;
  }

  const error = new Error(
    `The shared Inertia props do not match the contract HandleInertiaRequests sends. ` +
      `A page prop that reuses a shared prop's name replaces it for that page, and a ` +
      `renamed or removed shared prop reaches the browser the same way.\n${violations.join("\n")}`,
  );

  if (import.meta.env.DEV) {
    throw error;
  }

  report(error);
};
